import { apiService } from "./api";
import { API_CONFIG, buildUrl } from "../config/api";

export interface GuestOrderItem {
    product_id: string;
    product_name: string;
    quantity: number;
    unit_price: number;
    total_price: number;
}

export interface GuestOrderRequest {
    request_id: string;
    first_name: string;
    last_name: string | null;
    email: string | null;
    phone: string;
    customer_id: string | null;
    street_address: string;
    city: string;
    postal_code: string | null;
    payment_method: string;
    bkash_payment_id: string | null;
    bkash_trx_id: string | null;
    items_snapshot: string; // JSON string
    subtotal_amount: number;
    shipping_amount: number;
    /** The delivery area picked at checkout (its name is kept too). */
    delivery_area_id?: string | null;
    delivery_area_name?: string | null;
    total_amount: number;
    notes: string | null;
    status: "PENDING" | "APPROVED" | "REJECTED" | "PENDING_PAYMENT" | "PAYMENT_FAILED";
    converted_order_id: string | null;
    reviewed_by_user_id: string | null;
    reviewed_at: string | null;
    rejection_reason: string | null;
    create_time: string;
    update_time: string;
    // Where the shopper came from — absent from an older API.
    source?: RequestSource;
    utm_source?: string | null;
    utm_medium?: string | null;
    utm_campaign?: string | null;
    utm_content?: string | null;
    utm_term?: string | null;
    has_fbclid?: boolean;
    referrer_domain?: string | null;
    landing_path?: string | null;
    /** When this order's Purchase reached Meta (Conversions API). */
    meta_purchase_sent_at?: string | null;
}

/** A readable source, worked out by the API from the ad tags, Facebook
 * click id or referring site. */
export interface RequestSource {
    key: string;
    label: string;
    paid: boolean;
    campaign: string | null;
    ad: string | null;
}

export interface RequestSourceRow {
    source: string;
    source_key: string;
    paid: boolean;
    campaign: string | null;
    requests: number;
    approved: number;
    approved_revenue: number;
}

export interface RequestSourcesSummary {
    days: number;
    items: RequestSourceRow[];
    total_requests: number;
    total_approved: number;
    total_approved_revenue: number;
}

export interface GuestOrderRequestListResponse {
    items: GuestOrderRequest[];
    total: number;
    skip: number;
    limit: number;
}

export interface ApproveResult {
    request_id: string;
    status: string;
    converted_order_id: string;
    order_number: string;
    message: string;
}

export interface RejectResult {
    request_id: string;
    status: string;
    message: string;
}

export interface BulkDeleteResult {
    deleted_count: number;
    skipped_ids: string[];
}

export interface BkashRecheckResult {
    request_id: string;
    status: string;
    pending: boolean;
    message: string;
}

class GuestOrderService {
    async getRequests(params?: {
        skip?: number;
        limit?: number;
        status?: string;
    }): Promise<GuestOrderRequestListResponse> {
        const query = new URLSearchParams();
        if (params?.skip !== undefined)
            query.append("skip", String(params.skip));
        if (params?.limit !== undefined)
            query.append("limit", String(params.limit));
        if (params?.status) query.append("status", params.status);
        const qs = query.toString();
        const url = `${API_CONFIG.ENDPOINTS.GUEST_ORDERS.BASE}${qs ? `?${qs}` : ""}`;
        return apiService.get<GuestOrderRequestListResponse>(url);
    }

    /** Requests per source/campaign over the last `days` (placed, approved,
     * approved revenue). */
    async getSources(days: number): Promise<RequestSourcesSummary> {
        return apiService.get<RequestSourcesSummary>(
            `${API_CONFIG.ENDPOINTS.GUEST_ORDERS.BASE}/sources?days=${days}`,
        );
    }

    async getRequest(id: string): Promise<GuestOrderRequest> {
        return apiService.get<GuestOrderRequest>(
            `${API_CONFIG.ENDPOINTS.GUEST_ORDERS.BASE}/${id}`,
        );
    }

    async approve(id: string): Promise<ApproveResult> {
        const url = buildUrl(API_CONFIG.ENDPOINTS.GUEST_ORDERS.APPROVE, { id });
        return apiService.post<ApproveResult>(url, {});
    }

    async reject(id: string, reason?: string): Promise<RejectResult> {
        const url = buildUrl(API_CONFIG.ENDPOINTS.GUEST_ORDERS.REJECT, { id });
        return apiService.post<RejectResult>(url, { reason: reason || null });
    }

    async linkOrder(
        requestId: string,
        orderId: string,
    ): Promise<ApproveResult> {
        const url = buildUrl(API_CONFIG.ENDPOINTS.GUEST_ORDERS.LINK_ORDER, {
            id: requestId,
        });
        return apiService.post<ApproveResult>(url, { order_id: orderId });
    }

    // Lightweight list of request_ids only — powers "select all" without
    // pulling full rows (cart snapshots, etc.) for potentially thousands
    // of spam requests.
    async getRequestIds(status?: string): Promise<string[]> {
        const qs = status ? `?status=${encodeURIComponent(status)}` : "";
        const result = await apiService.get<{ request_ids: string[] }>(
            `${API_CONFIG.ENDPOINTS.GUEST_ORDERS.IDS}${qs}`,
        );
        return result.request_ids;
    }

    // Deletes only PENDING requests among the given IDs — anything already
    // APPROVED/REJECTED is protected server-side and comes back in
    // skipped_ids rather than being removed.
    async bulkDelete(requestIds: string[]): Promise<BulkDeleteResult> {
        return apiService.post<BulkDeleteResult>(
            API_CONFIG.ENDPOINTS.GUEST_ORDERS.BULK_DELETE,
            { request_ids: requestIds },
        );
    }

    // For a PENDING_PAYMENT request stuck in an inconclusive state (e.g.
    // the customer closed their browser before the callback page's own
    // execute call fired) — re-queries bKash for ground truth.
    async recheckBkashStatus(id: string): Promise<BkashRecheckResult> {
        const url = buildUrl(API_CONFIG.ENDPOINTS.GUEST_ORDERS.BKASH_RECHECK, { id });
        return apiService.post<BkashRecheckResult>(url, {});
    }

    parseItems(snapshot: string): GuestOrderItem[] {
        try {
            return JSON.parse(snapshot) as GuestOrderItem[];
        } catch {
            return [];
        }
    }
}

export const guestOrderService = new GuestOrderService();
